import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { formatMoney } from '@shared/lib/format';
import { currentLocale } from '@shared/i18n';
import { NamedIcon } from '@shared/ui/icons';

export interface Digest {
  handle: string | null;
  followers: number | null; followers_delta_30d: number | null;
  er: number | null; er_pctile: number | null; er_peer_scope: 'niche' | 'size' | null;
  reach: number | null; reach_delta_30d: number | null;
  suggested_price: number | null; my_price: number | null;
  reel_eng: number | null; photo_eng: number | null; reel_views: number | null;
  spark: number[] | null; spark_dates: string[] | null;
  collab: { handle: string; followers: number | null } | null;
  pitch_count: number | null;
  // How ad-heavy their feed is vs similar creators, what a mention is worth to a
  // brand, and their strongest posts — the buyer-side view, turned back on them.
  ad_load: number | null; ad_load_peers: number | null; posts_90d: number | null;
  story_ad_load: number | null; brand_lift_pct: number | null;
  top_posts: { id: string; permalink: string | null; thumb: string | null;
    views: number | null; likes: number | null; posted_at: string | null; type: string | null }[] | null;
}

// Catmull-Rom → cubic-bezier: turns the raw weekly points into a smooth curve.
function smoothPath(p: { x: number; y: number }[]): string {
  const d = [`M ${p[0].x.toFixed(1)},${p[0].y.toFixed(1)}`];
  for (let i = 1; i < p.length; i++) {
    const p0 = p[i - 2] ?? p[i - 1], p1 = p[i - 1], p2 = p[i], p3 = p[i + 1] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d.push(`C ${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`);
  }
  return d.join(' ');
}

// Follower sparkline (last few weekly snapshots): smooth line + gradient area, a
// left min/max scale (so a tiny swing isn't read as a crash), a "now" dot, and
// real dated x-axis ticks. Scales uniformly; stroke crisp via non-scaling-stroke.
function Spark({ series, locale }: { series: { d: string; v: number }[] | null; locale: string }) {
  if (!series || series.length < 2) return null;
  const data = series.map((s) => s.v);
  const w = 300, h = 70, gx = 36, padX = 6, topPad = 10, botPad = 24;
  const plotB = h - botPad;
  const min = Math.min(...data), max = Math.max(...data), rng = max - min || 1;
  const x0 = gx, x1 = w - padX;
  const yFor = (v: number) => plotB - ((v - min) / rng) * (plotB - topPad);
  const pts = series.map((s, i) => ({ x: x0 + (i / (series.length - 1)) * (x1 - x0), y: yFor(s.v), d: s.d }));
  const rising = data[data.length - 1] >= data[0];
  const stroke = rising ? '#37d67a' : '#e8607a';
  const gid = `spk-${rising ? 'up' : 'dn'}`;
  const line = smoothPath(pts);
  const last = pts[pts.length - 1];
  const area = `${line} L ${last.x.toFixed(1)},${plotB} L ${pts[0].x.toFixed(1)},${plotB} Z`;
  const yTop = yFor(max), yBot = yFor(min);
  const axisLine = { stroke: 'rgba(255,255,255,0.08)', strokeWidth: 1 } as const;
  const axisTxt = { fill: '#66707f', fontSize: 9, fontFamily: '-apple-system, Helvetica, Arial, sans-serif' } as const;
  const fmtDate = (iso: string) => {
    const dt = new Date(iso); return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };
  // Thin ticks so labels never collide: aim for ~4 across the width, always incl. ends.
  const step = Math.max(1, Math.ceil(pts.length / 4));
  const showTick = (i: number) => i === 0 || i === pts.length - 1 || i % step === 0;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 'auto', marginTop: 12, display: 'block', overflow: 'visible' }} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1={x0} y1={yTop} x2={w} y2={yTop} {...axisLine} strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />
      <line x1={x0} y1={yBot} x2={w} y2={yBot} {...axisLine} strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />
      <text x={gx - 6} y={yTop + 3} {...axisTxt} textAnchor="end">{fmtK(max)}</text>
      <text x={gx - 6} y={yBot + 3} {...axisTxt} textAnchor="end">{fmtK(min)}</text>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={last.x} cy={last.y} r="5.5" fill={stroke} opacity="0.18" />
      <circle cx={last.x} cy={last.y} r="2.8" fill={stroke} />
      {pts.map((p, i) => showTick(i) && (
        <text key={i} x={p.x} y={h - 5} {...axisTxt} textAnchor={i === 0 ? 'start' : i === pts.length - 1 ? 'end' : 'middle'}>{fmtDate(p.d)}</text>
      ))}
    </svg>
  );
}

const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1_000_000 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);

// The "how am I doing?" landing — a digest that surfaces the best of every pillar.
export default function CreatorHome() {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const nav = useNavigate();

  const q = useSupabaseQuery<Digest | null>(['creator-digest'], async () => supabase.rpc('creator_home_digest', {}));
  const d = q.data;
  // Shared caches with «Спрос» and «Бренды» — no extra requests when those were opened.
  const feedQ = useSupabaseQuery<{ id: string; title: string; brand_name: string | null }[]>(
    ['creator-campaign-feed'], async () => supabase.rpc('get_creator_campaign_feed'));
  const brandsQ = useSupabaseQuery<{ ok?: boolean; total?: number }>(['my-brand-history'], async () => supabase.rpc('get_my_brand_history'));

  if (q.isLoading) return <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 40 }}>…</div>;
  if (!d) return <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 40 }}>{tr('creator.home.noData', 'Данные появятся после первого сбора статистики.')}</div>;

  const up = (n: number | null) => n != null && n >= 0;
  const deltaChip = (n: number | null) => n == null ? null : (
    <span style={{ background: up(n) ? 'rgba(55,214,122,0.14)' : 'rgba(232,96,122,0.16)', color: up(n) ? '#37d67a' : '#e8607a',
      fontSize: 12, padding: '3px 9px', borderRadius: 20 }}>{up(n) ? '↑' : '↓'} {Math.abs(n)}% · 30д</span>
  );

  // one computed insight — the most notable true thing this period
  const insight = (() => {
    if (d.reach_delta_30d != null && d.reach_delta_30d >= 5) return tr('creator.home.insReach', 'Охват вырос на {{p}}% за месяц — так держать.', { p: Math.round(d.reach_delta_30d) });
    if (d.reel_eng && d.photo_eng && d.reel_eng > d.photo_eng * 1.15) return tr('creator.home.insReels', 'Ваши Рилсы собирают больше вовлечённости, чем фото — делайте больше Рилс.');
    if (d.followers_delta_30d != null && d.followers_delta_30d < 0) return tr('creator.home.insDrop', 'Подписчики немного снизились за месяц — чаще выкладывайте Рилс, они хорошо заходят.');
    if (d.followers_delta_30d != null && d.followers_delta_30d > 0) return tr('creator.home.insGrow', 'Вы выросли на {{p}}% за месяц — отличная динамика.', { p: d.followers_delta_30d });
    return tr('creator.home.insNeutral', 'Ваша статистика обновляется каждую неделю.');
  })();

  const priceMain = d.my_price ? formatMoney(d.my_price) : d.suggested_price ? `~${formatMoney(Math.round(d.suggested_price))}` : '—';
  const priceSub = (() => {
    if (d.my_price && d.suggested_price) {
      const r = d.my_price / d.suggested_price;
      if (r < 0.9) return { txt: tr('creator.home.priceUnder', 'занижена ~{{p}}%', { p: Math.round((1 - r) * 100) }), c: '#e8965a' };
      if (r > 1.1) return { txt: tr('creator.home.priceOver', 'выше рынка ~{{p}}%', { p: Math.round((r - 1) * 100) }), c: '#c9c96a' };
      return { txt: tr('creator.home.priceFair', 'по рынку'), c: '#8bd450' };
    }
    return { txt: d.suggested_price ? tr('creator.home.priceEst', 'наша оценка') : tr('creator.home.priceSet', 'укажите цену'), c: '#889' };
  })();

  // A tile that leads somewhere is a real <button> (keyboard + screen reader); a plain one stays a div.
  const tile = (icon: string, color: string, label: string, main: string, sub: string, onClick?: () => void) => {
    const Tag = onClick ? 'button' : 'div';
    return (
      <Tag {...(onClick ? { type: 'button' as const, onClick } : {})}
        style={{ display: 'block', width: '100%', textAlign: 'left', fontFamily: 'inherit', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 12, cursor: onClick ? 'pointer' : 'default' }}>
        <NamedIcon name={icon} size={18} color={color} />
        <span style={{ display: 'block', fontSize: 12, color: 'var(--dim)', marginTop: 6 }}>{label}</span>
        <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{main}</span>
        <span style={{ display: 'block', fontSize: 11, color: color, marginTop: 2 }}>{sub}</span>
      </Tag>
    );
  };

  const campaigns = feedQ.data ?? [];
  const brandsTotal = brandsQ.data?.ok ? brandsQ.data.total ?? 0 : 0;
  const link = (icon: string, color: string, title: string, sub: string, to: string) => (
    <button type="button" className="cr-link" onClick={() => nav(to)}>
      <NamedIcon name={icon} size={20} color={color} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{title}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginTop: 2, lineHeight: 1.4 }}>{sub}</span>
      </span>
      <span aria-hidden="true" style={{ color: 'var(--dim)', fontSize: 18 }}>›</span>
    </button>
  );

  // «Дом» answers «how am I doing and what's next». Detail lives on its own pages
  // (stats, brands, top reels) — the old feed was 8–9 blocks deep on a phone.
  return (
    <div className="cr-home">
      <div className="cr-home-top">
        <div>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--dim)' }}>{tr('creator.menu.followers', 'Подписчики')}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>{fmtK(d.followers)}</div>
              </div>
              {deltaChip(d.followers_delta_30d)}
            </div>
            <Spark
              series={d.spark && d.spark.length >= 2
                ? d.spark.map((v, i) => ({ d: (d.spark_dates ?? [])[i] ?? '', v })).slice(-5)
                : null}
              locale={currentLocale()} />
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>ER</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{d.er != null ? `${Number(d.er).toFixed(1)}%` : '—'}{d.er_pctile != null && <span style={{ fontSize: 11, color: '#37d67a' }}> топ-{Math.max(1, 100 - d.er_pctile)}%</span>}</div>
              </div>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>{tr('creator.menu.reach', 'Охват рилс')}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{fmtK(d.reach)}</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', background: 'rgba(55,214,122,0.10)', borderRadius: 12, padding: '11px 12px', marginTop: 12 }}>
            <NamedIcon name="megaphone" size={16} color="#37d67a" />
            <div style={{ fontSize: 13, color: '#8fe0a8', lineHeight: 1.5 }}>{insight}</div>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, color: 'var(--dim)', margin: '2px 2px 8px' }}>{tr('creator.home.brief', 'Кратко о вас')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {tile('money', '#e8965a', tr('creator.home.tilePrice', 'Ваша цена / рилс'), priceMain, priceSub.txt, () => nav('/provider/prices'))}
            {tile('trophy', '#4a9eff', tr('creator.home.tilePeers', 'Среди похожих'), d.er_pctile != null ? `Топ-${Math.max(1, 100 - d.er_pctile)}%` : '—',
              d.er_peer_scope === 'niche' ? tr('creator.home.byErNiche', 'по ER в вашей нише') : tr('creator.home.byErSize', 'по ER, ваш размер'),
              () => nav('/provider/stats#creator-peers'))}
            {tile('handshake', '#8a5cff', tr('creator.home.tileCollab', 'Коллаб для роста'), d.collab ? `@${d.collab.handle}` : '—', d.collab ? tr('creator.home.newAudience', 'новая аудитория') : tr('creator.home.soon', 'скоро'), () => nav('/provider/collabs'))}
            {tile('workers', '#37d67a', tr('creator.home.tileDemand', 'Бренды для питча'), d.pitch_count ? String(d.pitch_count) : '—', tr('creator.home.pitchSub', 'работают с похожими'), () => nav('/provider/demand'))}
          </div>

          <div style={{ marginTop: 12 }}>
            {link('megaphone', '#e0568f', tr('creator.home.campaigns', 'Задания брендов'),
              campaigns.length
                ? `${t('creator.home.campaignsN', { count: campaigns.length, defaultValue: '{{count}} открытых заданий' })} · ${campaigns[0].title}`
                : tr('creator.home.campaignsNone', 'Сейчас открытых заданий нет. Включите уведомления в «Спросе» — сообщим о новых.'),
              '/provider/demand')}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--dim)', margin: '20px 2px 8px' }}>{tr('creator.home.more', 'Подробнее')}</div>
      <div className="cr-links">
        {link('target', '#4a9eff', tr('creator.stats.title', 'Ваша статистика'),
          tr('creator.home.linkStatsSub', 'Реклама в постах, лучшие посты, форматы, место среди похожих'), '/provider/stats')}
        {link('workers', '#37d67a', tr('creator.home.linkBrands', 'Бренды'),
          brandsTotal
            ? t('creator.home.linkBrandsN', { count: brandsTotal, defaultValue: '{{count}} брендов, с которыми вы работали' })
            : tr('creator.home.linkBrandsSub', 'С кем вы работали и ваши посты о них'), '/provider/brands')}
        {link('play', '#8a5cff', tr('creator.home.linkTop', 'Топ рилс Кыргызстана'),
          tr('creator.home.linkTopSub', 'Что больше всего смотрят на этой неделе'), '/provider/top-reels')}
      </div>
    </div>
  );
}
